import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import DashboardLayout from './DashboardLayout';
import { SearchableSelect } from './SearchableSelect';
import { useGetActiveLocationsQuery } from '../../redux/employeeApi';
import { useGetOutletsQuery, useUpdateOutletMutation } from '../../redux/outletApi';
import {
  validateAddress,
  validateContactDetail,
  validateEmail,
  validateFssaiNumber,
  validateGstNumber,
  validateRequired,
} from '../../utils/validation';
import { resolveAssetUrl } from '../../utils/resolveAssetUrl';
import {
  normalizeWeekDay,
  toApiTime,
  toTimeInputValue,
  WEEK_DAY_OPTIONS,
} from '../../utils/outletSchedule';
import { AuthorizedMedia } from '../AuthorizedMedia';

const inputClasses =
  'mt-1 w-full rounded-lg border border-gray-200 bg-[#F7E5D5] px-3 py-2 text-sm placeholder:text-gray-700 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand';

function AdminOutletEdit() {
  const navigate = useNavigate();
  const { id } = useParams();
  const outletId = Number(id);

  const { data: outletsRes } = useGetOutletsQuery();
  const { data: locationsRes } = useGetActiveLocationsQuery();
  const [updateOutlet, { isLoading }] = useUpdateOutletMutation();

  const outlet = useMemo(() => {
    const list = outletsRes?.data || outletsRes || [];
    if (!Array.isArray(list)) return null;
    return list.find((o) => Number(o?.id) === outletId) || null;
  }, [outletsRes, outletId]);

  const outletLocationId = useMemo(() => {
    if (!outlet) return '';
    const raw =
      outlet?.location_id ??
      outlet?.locationId ??
      outlet?.location?.id ??
      outlet?.location_detail?.id ??
      outlet?.locationDetail?.id ??
      outlet?.location_detail_id ??
      outlet?.locationDetailId ??
      '';

    if (raw != null && raw !== '') return String(raw);

    // Fallback: `/api/outlets` may only return `location_name`.
    const outletName =
      String(
        outlet?.location_name ||
          outlet?.locationName ||
          outlet?.location?.name ||
          outlet?.location_detail?.name ||
          outlet?.locationDetail?.name ||
          outlet?.location ||
          ''
      )
        .trim()
        .toLowerCase();

    if (!outletName) return '';

    const locations = locationsRes?.data || [];
    const matched = Array.isArray(locations)
      ? locations.find(
          (loc) => String(loc?.name || '').trim().toLowerCase() === outletName
        )
      : null;

    return matched?.id != null ? String(matched.id) : '';
  }, [outlet, locationsRes]);

  const outletLocationLabel = useMemo(() => {
    if (!outlet) return '';
    return (
      outlet?.location_name ||
      outlet?.locationName ||
      outlet?.location?.name ||
      outlet?.location_detail?.name ||
      outlet?.locationDetail?.name ||
      outlet?.location ||
      ''
    );
  }, [outlet]);

  const locationOptions = useMemo(() => {
    const active = (locationsRes?.data || []).map((loc) => ({
      value: String(loc.id),
      label: loc.name ?? String(loc.id),
    }));

    if (!outletLocationId) return active;
    const exists = active.some((o) => String(o.value) === String(outletLocationId));
    if (exists) return active;

    // Outlet may belong to an inactive location (not in active list).
    return [
      {
        value: String(outletLocationId),
        label: outletLocationLabel || `Location #${outletLocationId}`,
      },
      ...active,
    ];
  }, [locationsRes, outletLocationId, outletLocationLabel]);

  const [form, setForm] = useState({
    location: '',
    email: '',
    contact: '',
    address: '',
    fssai: '',
    gst: '',
    google_url: '',
    swiggy_url: '',
    zomato_url: '',
    ordering_day: '',
    ordering_time: '',
    delivery_day: '',
    delivery_time: '',
    fssai_image: null,
    gst_image: null,
  });
  const [errors, setErrors] = useState({});

  const existingFssaiImageUrl = useMemo(() => {
    if (!outlet) return '';
    return (
      resolveAssetUrl(outlet?.fssai_image_url) ||
      resolveAssetUrl(outlet?.fssaiImageUrl) ||
      resolveAssetUrl(outlet?.fssai_file_url) ||
      resolveAssetUrl(outlet?.fssai_image) ||
      resolveAssetUrl(outlet?.fssaiImage) ||
      ''
    );
  }, [outlet]);

  const existingGstImageUrl = useMemo(() => {
    if (!outlet) return '';
    return (
      resolveAssetUrl(outlet?.gst_image_url) ||
      resolveAssetUrl(outlet?.gstImageUrl) ||
      resolveAssetUrl(outlet?.gst_file_url) ||
      resolveAssetUrl(outlet?.gst_image) ||
      resolveAssetUrl(outlet?.gstImage) ||
      ''
    );
  }, [outlet]);

  const fssaiPreviewUrl = useMemo(() => {
    if (!form.fssai_image) return '';
    try {
      return URL.createObjectURL(form.fssai_image);
    } catch {
      return '';
    }
  }, [form.fssai_image]);

  const gstPreviewUrl = useMemo(() => {
    if (!form.gst_image) return '';
    try {
      return URL.createObjectURL(form.gst_image);
    } catch {
      return '';
    }
  }, [form.gst_image]);

  useEffect(() => {
    return () => {
      if (fssaiPreviewUrl) URL.revokeObjectURL(fssaiPreviewUrl);
    };
  }, [fssaiPreviewUrl]);

  useEffect(() => {
    return () => {
      if (gstPreviewUrl) URL.revokeObjectURL(gstPreviewUrl);
    };
  }, [gstPreviewUrl]);

  const reqLabel = (text) => (
    <span className="inline-flex items-center gap-1.5 text-sm text-gray-700">
      <span className="min-w-0 leading-snug">{text}</span>
      <span className="shrink-0 text-lg leading-none text-red-600" aria-hidden="true">
        *
      </span>
    </span>
  );

  const didPrefillRef = useRef(false);

  useEffect(() => {
    if (!outlet) return;
    setForm((prev) => {
      const next = {
        ...prev,
        email: prev.email || outlet?.email || '',
        contact: prev.contact || outlet?.contact || '',
        address: prev.address || outlet?.address || '',
        fssai: prev.fssai || outlet?.fssai_number || '',
        gst: prev.gst || outlet?.gst_number || '',
        google_url: prev.google_url || outlet?.google_url || '',
        swiggy_url: prev.swiggy_url || outlet?.swiggy_url || '',
        zomato_url: prev.zomato_url || outlet?.zomato_url || '',
        ordering_day: prev.ordering_day || normalizeWeekDay(outlet?.ordering_day),
        ordering_time: prev.ordering_time || toTimeInputValue(outlet?.ordering_time),
        delivery_day: prev.delivery_day || normalizeWeekDay(outlet?.delivery_day),
        delivery_time: prev.delivery_time || toTimeInputValue(outlet?.delivery_time),
      };

      // Only prefill location if user hasn't picked one yet.
      if (!next.location && outletLocationId) next.location = outletLocationId;

      return next;
    });
    didPrefillRef.current = true;
  }, [outlet, outletLocationId]);

  const handleChange = (field) => (eOrValue) => {
    const nextValue =
      eOrValue && typeof eOrValue === 'object' && 'target' in eOrValue
        ? eOrValue.target?.value
        : eOrValue;

    let value = nextValue ?? '';
    if (field === 'email') {
      value = String(value).replace(/\s+/g, '').toLowerCase();
    }
    if (field === 'address') {
      value = String(value)
        .replace(/[^A-Za-z0-9 ,.\-/#()']/g, '')
        .slice(0, 100);
    }
    if (field === 'contact') {
      value = String(value).replace(/[^0-9]/g, '');
    }
    if (field === 'fssai') {
      value = String(value).replace(/[^0-9]/g, '').slice(0, 14);
    }
    if (field === 'gst') {
      value = String(value).toUpperCase().replace(/[^0-9A-Z]/g, '').slice(0, 15);
    }

    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const handleFileChange = (field) => (e) => {
    const file = e?.target?.files?.[0] || null;
    setForm((prev) => ({ ...prev, [field]: file }));
  };

  const handleClearSelected = (field) => () => {
    setForm((prev) => ({
      ...prev,
      ...(field === 'fssai' ? { fssai_image: null } : { gst_image: null }),
    }));
  };

  const validateForm = () => {
    const next = {};

    const locErr = validateRequired(form.location, 'Location');
    if (locErr) next.location = locErr;

    const emailErr = validateEmail(form.email, 'Email id');
    if (emailErr) next.email = emailErr;

    const contactErr = validateContactDetail(form.contact, { digits: 10 });
    if (contactErr) next.contact = contactErr;

    const addressErr = validateAddress(form.address, { maxLen: 100 });
    if (addressErr) next.address = addressErr;

    const fssaiErr = validateFssaiNumber(form.fssai);
    if (fssaiErr) next.fssai = fssaiErr;

    const gstErr = validateGstNumber(form.gst);
    if (gstErr) next.gst = gstErr;

    setErrors(next);
    const keys = Object.keys(next);
    if (keys.length === 0) return { ok: true };
    return { ok: false, firstError: next[keys[0]] };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (Number.isNaN(outletId)) return;
    const validation = validateForm();
    if (!validation.ok) {
      toast.error(
        validation.firstError || 'Please correct the highlighted fields.'
      );
      return;
    }
    try {
      const fd = new FormData();
      fd.append('outlet[address]', form.address);
      fd.append('outlet[contact]', form.contact);
      fd.append('outlet[email]', form.email);
      fd.append('outlet[fssai_number]', String(form.fssai || '').replace(/[\s-]/g, ''));
      fd.append('outlet[gst_number]', form.gst || '');
      fd.append('outlet[location_id]', form.location ? String(form.location) : '');
      fd.append('outlet[status]', String(Boolean(outlet?.status)));
      fd.append('outlet[google_url]', form.google_url || '');
      fd.append('outlet[swiggy_url]', form.swiggy_url || '');
      fd.append('outlet[zomato_url]', form.zomato_url || '');
      fd.append('outlet[ordering_day]', form.ordering_day || '');
      fd.append('outlet[ordering_time]', toApiTime(form.ordering_time));
      fd.append('outlet[delivery_day]', form.delivery_day || '');
      fd.append('outlet[delivery_time]', toApiTime(form.delivery_time));
      if (form.fssai_image) fd.append('outlet[fssai_image]', form.fssai_image);
      if (form.gst_image) fd.append('outlet[gst_image]', form.gst_image);

      await updateOutlet({
        id: outletId,
        outlet: fd,
      }).unwrap();
      toast.success('Outlet updated successfully.');
      navigate('/dashboard/admin/outlets');
    } catch (err) {
      toast.error(err?.data?.message || 'Failed to update outlet.');
    }
  };

  return (
    <DashboardLayout title="Edit Outlet Information">
      <div className="space-y-4 py-4">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <h2 className="text-lg font-bold text-brand decoration-brand/30 underline-offset-4">
          </h2>
          <div className="text-sm text-gray-500">
            <Link to="/dashboard/admin" className="text-brand hover:underline">
              Home
            </Link>
            {' / '}
            <Link
              to="/dashboard/admin/outlets"
              className="text-brand hover:underline"
            >
              Outlet
            </Link>
            {' / '}
            <span className="text-gray-400">Edit Outlet</span>
          </div>
        </div>

        <div className="rounded-2xl bg-white px-6 py-4 shadow-sm">
          {!outlet ? (
            <p className="text-sm text-gray-600">Loading outlet...</p>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <h3 className="text-xl font-semibold text-gray-800">
                Outlet Information
              </h3>

              <div className="grid gap-4 md:grid-cols-3">
                <label className="flex flex-col text-sm text-gray-700">
                  {reqLabel('Location')}
                  <SearchableSelect
                    name="location"
                    value={form.location}
                    onChange={handleChange('location')}
                    className={`${inputClasses} ${errors?.location ? 'border-red-500' : ''}`}
                    options={locationOptions}
                  />
                  {errors?.location ? (
                    <span className="mt-1 text-xs text-red-600">{errors.location}</span>
                  ) : null}
                </label>

                <label className="flex flex-col text-sm text-gray-700">
                  {reqLabel('Email Id')}
                  <input
                    type="text"
                    inputMode="email"
                    placeholder="Enter Email"
                    value={form.email}
                    onChange={handleChange('email')}
                    className={`${inputClasses} ${errors?.email ? 'border-red-500' : ''}`}
                  />
                  {errors?.email ? (
                    <span className="mt-1 text-xs text-red-600">{errors.email}</span>
                  ) : null}
                </label>

                <label className="flex flex-col text-sm text-gray-700">
                  {reqLabel('Contact Detail')}
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={10}
                    placeholder="Enter Contact"
                    value={form.contact}
                    onChange={handleChange('contact')}
                    className={`${inputClasses} ${errors?.contact ? 'border-red-500' : ''}`}
                  />
                  {errors?.contact ? (
                    <span className="mt-1 text-xs text-red-600">{errors.contact}</span>
                  ) : null}
                </label>

                <label className="flex flex-col text-sm text-gray-700 md:col-span-3">
                  {reqLabel('Address')}
                  <input
                    type="text"
                    placeholder="Enter Address"
                    value={form.address}
                    onChange={handleChange('address')}
                    maxLength={100}
                    className={`${inputClasses} ${errors?.address ? 'border-red-500' : ''}`}
                  />
                  <span className="mt-1 text-xs text-gray-500">
                    {String(form.address || '').length}/100
                  </span>
                  {errors?.address ? (
                    <span className="mt-1 text-xs text-red-600">{errors.address}</span>
                  ) : null}
                </label>

                <label className="flex flex-col text-sm text-gray-700">
                  FSSAI Number
                  <input
                    type="text"
                    placeholder="Enter FSSAI Number"
                    value={form.fssai}
                    onChange={handleChange('fssai')}
                    className={`${inputClasses} ${errors?.fssai ? 'border-red-500' : ''}`}
                  />
                  {errors?.fssai ? (
                    <span className="mt-1 text-xs text-red-600">{errors.fssai}</span>
                  ) : null}
                </label>

                <label className="flex flex-col text-sm text-gray-700">
                  GST Number
                  <input
                    type="text"
                    placeholder="Enter GST Number"
                    value={form.gst}
                    onChange={handleChange('gst')}
                    className={`${inputClasses} ${errors?.gst ? 'border-red-500' : ''}`}
                  />
                  {errors?.gst ? (
                    <span className="mt-1 text-xs text-red-600">{errors.gst}</span>
                  ) : null}
                </label>

                <label className="flex flex-col text-sm text-gray-700">
                  Ordering Day
                  <SearchableSelect
                    name="ordering_day"
                    value={form.ordering_day}
                    onChange={handleChange('ordering_day')}
                    className={inputClasses}
                    options={WEEK_DAY_OPTIONS}
                    placeholder="Select day"
                  />
                </label>

                <label className="flex flex-col text-sm text-gray-700">
                  Ordering Time
                  <input
                    type="time"
                    value={form.ordering_time}
                    onChange={handleChange('ordering_time')}
                    className={inputClasses}
                  />
                </label>

                <label className="flex flex-col text-sm text-gray-700">
                  Delivery Day
                  <SearchableSelect
                    name="delivery_day"
                    value={form.delivery_day}
                    onChange={handleChange('delivery_day')}
                    className={inputClasses}
                    options={WEEK_DAY_OPTIONS}
                    placeholder="Select day"
                  />
                </label>

                <label className="flex flex-col text-sm text-gray-700">
                  Delivery Time
                  <input
                    type="time"
                    value={form.delivery_time}
                    onChange={handleChange('delivery_time')}
                    className={inputClasses}
                  />
                </label>

                <label className="flex flex-col text-sm text-gray-700">
                  Google URL
                  <input
                    type="text"
                    placeholder="Enter Google URL"
                    value={form.google_url}
                    onChange={handleChange('google_url')}
                    className={inputClasses}
                  />
                </label>

                <label className="flex flex-col text-sm text-gray-700">
                  Swiggy URL
                  <input
                    type="text"
                    placeholder="Enter Swiggy URL"
                    value={form.swiggy_url}
                    onChange={handleChange('swiggy_url')}
                    className={inputClasses}
                  />
                </label>

                <label className="flex flex-col text-sm text-gray-700">
                  Zomato URL
                  <input
                    type="text"
                    placeholder="Enter Zomato URL"
                    value={form.zomato_url}
                    onChange={handleChange('zomato_url')}
                    className={inputClasses}
                  />
                </label>

                <label className="flex flex-col text-sm text-gray-700">
                  FSSAI Image
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange('fssai_image')}
                    className={inputClasses}
                  />
                  <div className="mt-2 flex flex-wrap items-center gap-3">
                    {fssaiPreviewUrl ? (
                      <div className="flex items-center gap-3">
                        <img
                          src={fssaiPreviewUrl}
                          alt="Selected FSSAI"
                          className="h-16 w-16 rounded-md border border-gray-200 object-cover"
                        />
                        <button
                          type="button"
                          onClick={handleClearSelected('fssai')}
                          className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50"
                        >
                          Clear selected
                        </button>
                      </div>
                    ) : null}

                    {!fssaiPreviewUrl && existingFssaiImageUrl ? (
                      <div className="flex items-center gap-3">
                        <AuthorizedMedia
                          src={existingFssaiImageUrl}
                          alt="Current FSSAI"
                          className="h-16 w-16 rounded-md border border-gray-200 object-cover"
                        />
                      </div>
                    ) : null}
                  </div>
                </label>

                <label className="flex flex-col text-sm text-gray-700">
                  GST Image
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange('gst_image')}
                    className={inputClasses}
                  />
                  <div className="mt-2 flex flex-wrap items-center gap-3">
                    {gstPreviewUrl ? (
                      <div className="flex items-center gap-3">
                        <img
                          src={gstPreviewUrl}
                          alt="Selected GST"
                          className="h-16 w-16 rounded-md border border-gray-200 object-cover"
                        />
                        <button
                          type="button"
                          onClick={handleClearSelected('gst')}
                          className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50"
                        >
                          Clear selected
                        </button>
                      </div>
                    ) : null}

                    {!gstPreviewUrl && existingGstImageUrl ? (
                      <div className="flex items-center gap-3">
                        <AuthorizedMedia
                          src={existingGstImageUrl}
                          alt="Current GST"
                          className="h-16 w-16 rounded-md border border-gray-200 object-cover"
                        />
                      </div>
                    ) : null}
                  </div>
                </label>

              </div>

              <div className="flex justify-start gap-5 pt-4">
                <button
                  type="submit"
                  disabled={isLoading}
                  className="inline-flex min-w-[140px] items-center justify-center rounded-lg bg-brand px-8 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-red-800 disabled:opacity-60"
                >
                  {isLoading ? 'Saving...' : 'Update'}
                </button>

                <button
                  type="button"
                  onClick={() => navigate('/dashboard/admin/outlets')}
                  className="inline-flex min-w-[140px] items-center justify-center rounded-lg border border-brand bg-white px-8 py-2.5 text-sm font-semibold text-brand hover:bg-gray-100"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}

export default AdminOutletEdit;

